import { analytics } from '../analytics/Analytics.js';

// V1.7 (4.19) — toggleable client-side perf overlay for diagnosing the
// multiplayer-stutter playtest finding. Hidden by default; press F3 to
// toggle. Tracks three signals so we can triage where stutter actually
// comes from:
//
//   - frameMs           — wall-clock between rAF callbacks (render-side
//                          jitter; spikes mean a long client frame).
//   - snapshotIntervalMs — wall-clock between consecutive onSnapshot
//                          callbacks (network + server-tick jitter; on a
//                          healthy 20 Hz feed this clusters at 50 ms).
//   - snapshotLagMs      — drift of (wall - expected) where expected =
//                          firstWall + (snap.tick - firstTick) * TICK_MS.
//                          Positive and growing = server tick is falling
//                          behind real time (server tick lag, not
//                          network jitter).
//
// Each metric is a ring buffer of the last N=120 samples (~6 s at 20 Hz
// snapshots, ~2 s at 60 fps frames). p50/p99/max are computed cheaply by
// sorting the snapshot. Rendering is throttled to ~5 Hz so the overlay
// doesn't itself impact frame time.
//
// Rule 2 — read-only of sim state. The overlay never writes back; the
// `setTick` setter is just to print the current sim tick alongside.
// Rule 7 — DOM only; no Phaser scene state.

const TICK_MS = 50; // mirrors @signalclash/shared State.TICK_MS

const BUFFER_SIZE = 120;
const RENDER_THROTTLE_MS = 200;

// V4.1 — sustained-drop detection. A frame slower than FRAME_MS_LIMIT
// counts as a "drop"; SUSTAINED_DROP_MS of accumulated drops within
// SUSTAIN_WINDOW_MS triggers an emit. EMIT_COOLDOWN_MS caps the event
// frequency so a sustained slowdown produces at most one event per
// window — analytics is for triage, not for streaming-quality
// monitoring.
const FRAME_MS_LIMIT = 22; // ~45 fps and below
const SUSTAIN_WINDOW_MS = 3_000;
const SUSTAINED_DROP_MS = 1_500;
const EMIT_COOLDOWN_MS = 30_000;

interface MetricBuffer {
  samples: number[];
  index: number;
  filled: boolean;
}

function makeBuffer(): MetricBuffer {
  return { samples: new Array<number>(BUFFER_SIZE).fill(0), index: 0, filled: false };
}

function push(buf: MetricBuffer, value: number): void {
  buf.samples[buf.index] = value;
  buf.index = (buf.index + 1) % BUFFER_SIZE;
  if (buf.index === 0) buf.filled = true;
}

interface MetricSummary {
  p50: number;
  p99: number;
  max: number;
  last: number;
  count: number;
}

function summarize(buf: MetricBuffer): MetricSummary {
  const count = buf.filled ? BUFFER_SIZE : buf.index;
  if (count === 0) return { p50: 0, p99: 0, max: 0, last: 0, count: 0 };
  const sorted = buf.samples.slice(0, count).sort((a, b) => a - b);
  const p50Idx = Math.floor(count * 0.5);
  const p99Idx = Math.min(count - 1, Math.floor(count * 0.99));
  const last = buf.index === 0 ? buf.samples[BUFFER_SIZE - 1]! : buf.samples[buf.index - 1]!;
  return {
    p50: sorted[p50Idx]!,
    p99: sorted[p99Idx]!,
    max: sorted[count - 1]!,
    last,
    count,
  };
}

export class PerfOverlay {
  private readonly el: HTMLDivElement;
  private visible = false;
  private fps = 0;
  private tick = 0;

  private readonly frame = makeBuffer();
  private readonly snapshotInterval = makeBuffer();
  private readonly snapshotLag = makeBuffer();

  private lastFrameMs: number | null = null;
  private lastSnapshotMs: number | null = null;
  private firstSnapshotWallMs: number | null = null;
  private firstSnapshotTick: number | null = null;

  private lastRenderMs = 0;
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;

  // V4.1 — sustained-low-FPS detector. The CrazyGames "playtime" metric
  // implicitly favours games that don't stutter, but the dashboard
  // doesn't surface frame-rate signals. We rolling-average the last
  // ~3 seconds of frame intervals and emit `perf_frame_drop_sustained`
  // when the average drops below FPS_DROP_THRESHOLD. Each emission is
  // gated by EMIT_COOLDOWN_MS so a sustained slowdown produces a
  // bounded number of events instead of one per frame.
  //
  // Only fires after the buffer is genuinely populated (≥ 60 samples)
  // so the seed period of a session doesn't fire false positives.
  private framesSinceEmit = 0;
  private lastDropEmitMs = 0;
  private dropAccumulator = 0;
  private dropAccumStartMs: number | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'perf-overlay';
    this.el.style.cssText = [
      'position:absolute',
      'top:12px',
      'right:12px',
      'z-index:40',
      'display:none',
      'min-width:200px',
      'padding:8px 12px',
      'background:rgba(8,10,14,0.92)',
      'border:1px solid #2c3a48',
      'border-radius:4px',
      'font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
      'font-size:11px',
      'line-height:1.45',
      'color:#cfd6dd',
      'pointer-events:none',
      'white-space:pre',
      'text-shadow:0 0 6px rgba(0,0,0,0.6)',
    ].join(';');
    document.body.appendChild(this.el);

    this.keyHandler = (e: KeyboardEvent): void => {
      if (e.key !== 'F3') return;
      // Allow when no input is focused; F3 is a browser refresh shortcut
      // in IE-era legacy code paths, but modern browsers don't bind it.
      // CrazyGames iframe inherits the page's keydown so this is safe.
      e.preventDefault();
      this.toggle();
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  toggle(): void {
    this.visible = !this.visible;
    this.el.style.display = this.visible ? 'block' : 'none';
    if (this.visible) this.render();
  }

  setFps(fps: number): void {
    this.fps = fps;
  }

  setTick(tick: number): void {
    this.tick = tick;
  }

  // Called from ClientLoop.tick — receives the rAF timestamp. Computes
  // wall-clock delta to the previous frame; the first call seeds.
  recordFrame(nowMs: number): void {
    if (this.lastFrameMs !== null) {
      const delta = nowMs - this.lastFrameMs;
      if (delta > 0 && delta < 1000) {
        push(this.frame, delta);
        this.framesSinceEmit += 1;
        this.checkSustainedDrop(nowMs, delta);
      }
    }
    this.lastFrameMs = nowMs;
    this.maybeRender(nowMs);
  }

  // V4.1 — accumulate "drop time" within a rolling SUSTAIN_WINDOW_MS.
  // When the player loses ≥ SUSTAINED_DROP_MS of frame time within the
  // window, emit one `perf_frame_drop_sustained` event then enter a
  // EMIT_COOLDOWN_MS quiet period. The accumulator is reset whenever
  // the window expires without crossing the threshold OR the cooldown
  // is active.
  private checkSustainedDrop(nowMs: number, deltaMs: number): void {
    if (nowMs - this.lastDropEmitMs < EMIT_COOLDOWN_MS) {
      this.dropAccumStartMs = null;
      this.dropAccumulator = 0;
      return;
    }
    if (deltaMs <= FRAME_MS_LIMIT) {
      // Healthy frame — don't reset the window, but don't extend it.
      // A few healthy frames mixed into a stutter still count.
      return;
    }
    if (this.dropAccumStartMs === null) {
      this.dropAccumStartMs = nowMs;
      this.dropAccumulator = 0;
    }
    this.dropAccumulator += deltaMs;
    if (nowMs - this.dropAccumStartMs > SUSTAIN_WINDOW_MS) {
      // Window expired without crossing — reset and start a new window
      // anchored at this drop.
      this.dropAccumStartMs = nowMs;
      this.dropAccumulator = deltaMs;
      return;
    }
    if (this.dropAccumulator >= SUSTAINED_DROP_MS) {
      const windowDurationMs = Math.max(1, nowMs - this.dropAccumStartMs);
      // Reconstruct an approximate FPS over the drop window. fps = (good
      // frame count / window seconds); we don't track good-frame counts
      // separately, so we approximate from accumulated drop time vs
      // window time.
      const approxFps = Math.round(
        Math.max(1, (windowDurationMs - this.dropAccumulator) / 16.67),
      );
      analytics.emit({
        kind: 'perf_frame_drop_sustained',
        atSec: Math.round(nowMs / 1000),
        fps: approxFps,
        durationMs: Math.round(windowDurationMs),
      });
      this.lastDropEmitMs = nowMs;
      this.dropAccumStartMs = null;
      this.dropAccumulator = 0;
    }
  }

  // Called from the onSnapshot subscription. We don't have the snapshot's
  // wall-clock arrival from the network layer, so we use performance.now()
  // at the moment of the JS callback — that's the same place stutter
  // would visibly originate from anyway.
  recordSnapshot(snapTick: number): void {
    const now = performance.now();
    if (this.lastSnapshotMs !== null) {
      const interval = now - this.lastSnapshotMs;
      if (interval > 0 && interval < 5000) push(this.snapshotInterval, interval);
    }
    this.lastSnapshotMs = now;
    if (this.firstSnapshotWallMs === null || this.firstSnapshotTick === null) {
      this.firstSnapshotWallMs = now;
      this.firstSnapshotTick = snapTick;
    } else {
      const expected = this.firstSnapshotWallMs + (snapTick - this.firstSnapshotTick) * TICK_MS;
      const lag = now - expected;
      // Negative lag is OK (snapshots arriving slightly early due to clock
      // drift); clamp the absolute value so the buffer doesn't explode on
      // a one-off pause-resume jump.
      if (Math.abs(lag) < 10000) push(this.snapshotLag, lag);
    }
  }

  dispose(): void {
    if (this.keyHandler !== null) {
      window.removeEventListener('keydown', this.keyHandler);
      this.keyHandler = null;
    }
    this.el.remove();
  }

  private maybeRender(nowMs: number): void {
    if (!this.visible) return;
    if (nowMs - this.lastRenderMs < RENDER_THROTTLE_MS) return;
    this.lastRenderMs = nowMs;
    this.render();
  }

  private render(): void {
    const f = summarize(this.frame);
    const s = summarize(this.snapshotInterval);
    const l = summarize(this.snapshotLag);
    this.el.textContent =
      `PERF (F3 to hide)\n` +
      `FPS         ${this.fps}\n` +
      `Tick        ${this.tick}\n` +
      `\n` +
      `Frame ms    last ${f.last.toFixed(1)}\n` +
      `            p50  ${f.p50.toFixed(1)}\n` +
      `            p99  ${f.p99.toFixed(1)}\n` +
      `            max  ${f.max.toFixed(1)}\n` +
      `\n` +
      `Snap Δt ms  last ${s.last.toFixed(0)}\n` +
      `(target 50) p50  ${s.p50.toFixed(0)}\n` +
      `            p99  ${s.p99.toFixed(0)}\n` +
      `            max  ${s.max.toFixed(0)}\n` +
      `\n` +
      `Snap lag ms last ${l.last.toFixed(0)}\n` +
      `            p50  ${l.p50.toFixed(0)}\n` +
      `            p99  ${l.p99.toFixed(0)}\n` +
      `(grows = server tick falling behind)`;
  }
}
